import {
  CheckCircle as CheckCircleIcon,
  KeyboardArrowDown,
  KeyboardArrowUp,
  MoreVert as MoreVertIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon,
} from '@mui/icons-material';
import { Box, Chip, Collapse, IconButton, Tab, TableCell, TableRow, Tabs, Typography } from '@mui/material';
import { format } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';

import * as voiceLib from 'entities/voice/lib';

import { Voices } from 'shared/api';
import { getLanguageName } from 'shared/config';

import { VoiceAvatar } from './voice-avatar';

type Props = {
  voice: Voices;
  columnCount: number;
  isExpanded: boolean;
  onToggle: (id: string) => void;
  onMenuOpen: (event: React.MouseEvent<HTMLElement>, voice: Voices) => void;
  onAvatarOpen: (voice: Voices) => void;
};

const formatDate = (value?: string): string => (value ? format(new Date(value), 'yyyy-MM-dd') : '—');

export const VoiceRow = ({ voice, columnCount, isExpanded, onToggle, onMenuOpen, onAvatarOpen }: Props) => {
  const bioLanguages = useMemo(() => voiceLib.getFilledLanguages(voice.bio), [voice.bio]);
  // Языки внутри раскрытия — объединение того, где есть хоть какой-то текст.
  const detailLanguages = useMemo(
    () => Array.from(new Set([...bioLanguages, ...voiceLib.getFilledLanguages(voice.specialty)])),
    [bioLanguages, voice.specialty],
  );
  const [detailLanguage, setDetailLanguage] = useState(detailLanguages[0] ?? 'en');
  const detailsId = `voice-details-${voice._id}`;
  const status = voiceLib.getVoiceStatus(voice.status);

  useEffect(() => {
    if (!detailLanguages.includes(detailLanguage)) {
      setDetailLanguage(detailLanguages[0] ?? 'en');
    }
  }, [detailLanguages, detailLanguage]);

  return (
    <>
      <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => onToggle(voice._id)}>
        <TableCell sx={{ width: 48 }}>
          <IconButton
            size="small"
            aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
            aria-expanded={isExpanded}
            aria-controls={detailsId}
          >
            {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell align="left">
          <Box display="flex" alignItems="center" gap={1.5}>
            <VoiceAvatar voice={voice} onOpen={onAvatarOpen} />
            {voice.name}
          </Box>
        </TableCell>
        <TableCell align="left">
          <Box display="flex" alignItems="center" gap={1}>
            <img
              src={`https://flagcdn.com/w20/${voice.countryCode.toLowerCase()}.png`}
              alt={voice.countryCode}
              width={20}
              height={15}
              style={{ objectFit: 'cover' }}
            />
            {voice.countryCode.toUpperCase()}
          </Box>
        </TableCell>
        <TableCell align="left">{voice.languages.map(getLanguageName).join(', ')}</TableCell>
        <TableCell align="left">{voiceLib.resolveLocalized(voice.specialty, 'en') || '—'}</TableCell>
        <TableCell align="left">
          {bioLanguages.length ? (
            <Box display="flex" gap={0.5} flexWrap="wrap">
              {bioLanguages.map((code) => (
                <Chip key={code} label={code.toUpperCase()} size="small" variant="outlined" />
              ))}
            </Box>
          ) : (
            '—'
          )}
        </TableCell>
        <TableCell align="left">
          <Chip
            label={voiceLib.VOICE_STATUS_LABELS[status]}
            color={voiceLib.VOICE_STATUS_COLORS[status]}
            size="small"
            sx={{ whiteSpace: 'nowrap' }}
          />
        </TableCell>
        <TableCell align="center">
          {voice.isPublished ? (
            <CheckCircleIcon color="success" fontSize="small" titleAccess="Published" />
          ) : (
            <RemoveCircleOutlineIcon color="disabled" fontSize="small" titleAccess="Not published" />
          )}
        </TableCell>
        <TableCell align="left">
          {voice.notes?.trim() ? (
            <Typography
              variant="body2"
              sx={{
                maxWidth: 240,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                wordBreak: 'break-word',
              }}
            >
              {voice.notes}
            </Typography>
          ) : (
            '—'
          )}
        </TableCell>
        <TableCell align="left">{formatDate(voice.updatedAt)}</TableCell>
        <TableCell align="center" onClick={(event) => event.stopPropagation()}>
          <IconButton size="small" onClick={(event) => onMenuOpen(event, voice)} aria-label="more options">
            <MoreVertIcon />
          </IconButton>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell sx={{ py: 0, border: 0 }} colSpan={columnCount}>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box id={detailsId} sx={{ px: 3, py: 2, bgcolor: 'action.hover' }}>
              {detailLanguages.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No bio or specialty yet
                </Typography>
              ) : (
                <>
                  <Tabs
                    value={detailLanguage}
                    onChange={(_, value: string) => setDetailLanguage(value)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ minHeight: 36, mb: 1.5 }}
                  >
                    {detailLanguages.map((code) => (
                      <Tab
                        key={code}
                        value={code}
                        label={getLanguageName(code)}
                        sx={{ minHeight: 36, textTransform: 'none' }}
                      />
                    ))}
                  </Tabs>

                  <Typography variant="caption" color="text.secondary">
                    Specialty
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1.5 }}>
                    {voiceLib.getLocalizedText(voice.specialty, detailLanguage) || '—'}
                  </Typography>

                  <Typography variant="caption" color="text.secondary">
                    Bio
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {voiceLib.getLocalizedText(voice.bio, detailLanguage) || '—'}
                  </Typography>
                </>
              )}

              {voice.notes?.trim() && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Notes
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {voice.notes}
                  </Typography>
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};
